import { compressImage, uploadImageToCloudinary } from './helpers';
import {
  getR2WorkerUrl,
  getR2AuthKey,
  saveR2WorkerConfig,
  clearR2WorkerConfig,
} from './migrationHelper';

export {
  getR2WorkerUrl,
  getR2AuthKey,
  saveR2WorkerConfig,
  clearR2WorkerConfig,
};

/**
 * Upload high-resolution documents (Receipts, Orders, Risk Reports) to Cloudflare R2
 * with zero-downtime automatic fallback to Cloudinary if R2 is not configured or encounters an error.
 *
 * @param {File|Blob} file - The file to upload (Image, PDF, etc.)
 * @param {string} [folder='receipts'] - Target folder ('receipts', 'orders', 'risk_reports', etc.)
 * @param {object} [options={}] - Compression and upload options
 * @returns {Promise<string>} - Resolves to the public URL of the uploaded document
 */
export const uploadDocumentToR2 = async (file, folder = 'receipts', options = {}) => {
  const { maxWidth = 2560, maxHeight = 2560, quality = 0.88 } = options;

  let preparedFile = file;
  if (file && file.type && file.type.startsWith('image/')) {
    try {
      preparedFile = await compressImage(file, maxWidth, maxHeight, quality);
    } catch (e) {
      console.warn('[Storage Router] Image compression warning, continuing with original file:', e);
      preparedFile = file;
    }
  }

  const workerUrl = getR2WorkerUrl();
  const authKey = getR2AuthKey();

  // If Cloudflare R2 Worker URL is configured, attempt direct upload
  if (workerUrl && typeof workerUrl === 'string' && workerUrl.trim() !== '') {
    try {
      const cleanUrl = workerUrl.trim().replace(/\/+$/, '');
      const endpoint = `${cleanUrl}/upload?folder=${encodeURIComponent(folder)}`;

      const formData = new FormData();
      formData.append('file', preparedFile);

      const headers = {};
      if (authKey) {
        headers['X-Custom-Auth-Key'] = authKey;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`R2 Worker returned HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      if (data && data.success && data.url) {
        return data.url;
      }

      throw new Error((data && data.error) || 'Invalid response from R2 Worker');
    } catch (err) {
      console.warn(
        '[Storage Router] R2 upload failed, seamlessly falling back to Cloudinary:',
        err
      );
    }
  } else {
    console.log('[Storage Router] R2 Worker URL not set, seamlessly falling back to Cloudinary...');
  }

  // Graceful Fallback: Upload to Cloudinary
  return await uploadImageToCloudinary(preparedFile, {
    maxWidth,
    maxHeight,
    quality,
    ...options,
    slot: 'document',
  });
};

/**
 * Convenience helper to upload receipt/delivery evidence
 */
export const uploadReceipt = (file, options = {}) => {
  return uploadDocumentToR2(file, 'receipts', options);
};

/**
 * Convenience helper to upload purchase order documents
 */
export const uploadOrderDocument = (file, options = {}) => {
  return uploadDocumentToR2(file, 'orders', options);
};

/**
 * Convenience helper to upload clinical or operational risk reports
 */
export const uploadRiskReport = (file, options = {}) => {
  return uploadDocumentToR2(file, 'risk_reports', options);
};

/**
 * Convenience helper to upload item master images
 */
export const uploadItemImage = (file, options = {}) => {
  return uploadDocumentToR2(file, 'items', options);
};

/**
 * Convenience helper to upload instrument tag images
 */
export const uploadTagImage = (file, options = {}) => {
  return uploadDocumentToR2(file, 'tags', options);
};

/**
 * Convenience helper to upload recall / defective item evidence images
 */
export const uploadRecallImage = (file, options = {}) => {
  return uploadDocumentToR2(file, 'recalls', options);
};
